/**
 * The key.
 *
 * Not optional chrome. A collapsed lane says what state a project is in using
 * colour, and colour without a key is a private code - the workbook's readers had
 * six labelled task rows per project and could see the state by reading; this chart
 * trades that for one row and therefore owes an explanation somewhere on screen.
 *
 * It also spells out the three fill treatments, because the difference between a
 * solid bar, a hatched bar and a pale bar is the difference between "60% done",
 * "nobody has recorded how far along this is" and "not started" - and that
 * distinction is the main thing this tool has that the spreadsheet did not.
 */

import styled from 'styled-components';

import { palette, radius, STATE_STYLE, type PhaseState } from '../styles/theme';

const Wrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 18px;
  font-size: 12px;
  color: ${palette.inkSoft};
`;

const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
`;

const Divider = styled.span`
  width: 1px;
  height: 18px;
  background: ${palette.border};
`;

const Item = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: ${palette.ink};
  white-space: nowrap;
`;

const Swatch = styled.span<{ $fill: string }>`
  width: 26px;
  height: 12px;
  border-radius: ${radius.pill};
  background: ${(p) => p.$fill};
  border: 1px solid rgba(46, 21, 36, 0.12);
`;

const PaleSwatch = styled(Swatch)`
  background: ${(p) => p.$fill}40;
  border-color: ${(p) => p.$fill};
`;

const HatchedSwatch = styled(Swatch)`
  background: ${(p) => p.$fill}40;
  border-color: ${(p) => p.$fill};
  background-image: repeating-linear-gradient(
    135deg,
    ${(p) => p.$fill} 0,
    ${(p) => p.$fill} 3px,
    transparent 3px,
    transparent 9px
  );
`;

const DiamondSwatch = styled.span<{ $fill: string }>`
  width: 11px;
  height: 11px;
  margin: 0 8px;
  background: ${(p) => p.$fill};
  border-radius: 3px;
  transform: rotate(45deg);
`;

/**
 * Ordered most advanced first, matching the precedence in utils/phaseState.ts, so
 * the key reads as the ranking it actually is rather than as an arbitrary list.
 */
const ORDER: PhaseState[] = ['coding', 'architecting', 'wireframes', 'planning', 'other'];

export default function Legend() {
  return (
    <Wrap>
      <Group>
        {ORDER.map((state) => (
          <Item key={state}>
            <Swatch $fill={STATE_STYLE[state].fill} aria-hidden />
            {/* "Other work" covers Testing, Maintenance and the one-off workstream
                names. Named explicitly so nobody reads grey as "broken". */}
            {STATE_STYLE[state].label}
          </Item>
        ))}
      </Group>

      <Divider aria-hidden />

      <Group>
        <Item>
          <Swatch $fill={palette.hotPink} aria-hidden />
          done
        </Item>
        <Item>
          <PaleSwatch $fill={palette.hotPink} aria-hidden />
          remaining
        </Item>
        <Item>
          <HatchedSwatch $fill={palette.hotPink} aria-hidden />
          progress not recorded
        </Item>
        <Item>
          <DiamondSwatch $fill={palette.hotPink} aria-hidden />
          single day
        </Item>
      </Group>
    </Wrap>
  );
}
